/* eslint-disable @typescript-eslint/no-explicit-any */
import { 
  fetchCloudPerformance,
  fetchConnectionPerformance,
  setTimeRange as setCloudTimeRange,
  setInterval as setCloudInterval,
  setUseCustomDateRange as setCloudUseCustomDateRange,
  setCustomDateRange as setCloudCustomDateRange,
  clearCustomDateRange as clearCloudCustomDateRange,
  setMaxPoints as setCloudMaxPoints,
} from "@redux/slices/kontrolEdge/connectionSlice";
import { 
  fetchPortPerformance,
  setTimeRange as setPortTimeRange,
  setInterval as setPortInterval,
  setUseCustomDateRange as setPortUseCustomDateRange,
  setCustomDateRange as setPortCustomDateRange,
  clearCustomDateRange as clearPortCustomDateRange,
  setMaxPoints as setPortMaxPoints,
} from "@redux/slices/kontrolEdge/portSlice";
import {
fetchDatacenterPerformance,
setTimeRange as setDatacenterTimeRange,
setInterval as setDatacenterInterval,
setUseCustomDateRange as setDatacenterUseCustomDateRange,
setCustomDateRange as setDatacenterCustomDateRange,
clearCustomDateRange as clearDatacenterCustomDateRange,
setMaxPoints as setDatacenterMaxPoints,
} from "@redux/slices/kontrolEdge/datacenterSlice";
import { RootState } from "@redux/store/store";


export type BandwidthService = "port" | "cloud" | "internet" | "datacenter";

export type Duration = `${number}${"s" | "m" | "h" | "d"}`;

export interface BandwidthSliceShape {
timeRange: Duration | null;   // allow null until initialized
interval: Duration | null;
performanceData: any | null;
performanceLoading: boolean;
performanceError: string | null;
initialized?: boolean;        // optional, if you added it
// Custom date range mode
useCustomDateRange?: boolean;
customStartDate?: string | null; // ISO string
customEndDate?: string | null;   // ISO string
maxPoints?: number;
}

export interface FetchArgsCommon {
api: any;
tenantId: number;
timeRange?: string;  // Optional when using custom date range
interval: string;
destination?: string;
startDate?: string;  // ISO string
endDate?: string;    // ISO string
maxPoints?: number;
}

export interface ServiceAdapter {
// how to read this service state from the store
select: (s: RootState) => BandwidthSliceShape;

// how to fetch for this service
fetchThunk: (args: FetchArgsCommon & { id: string }) => any;

// how to update controls in this service
setTimeRange: (v: Duration) => any;
setInterval: (v: Duration) => any;

// optional helper to set both at once. If not present, chart will fallback to individual sets.
setTimeControls?: (p: { timeRange: Duration; interval: Duration }) => any;

// Custom date range actions
setUseCustomDateRange: (v: boolean) => any;
setCustomDateRange: (p: { start: string; end: string }) => any;
clearCustomDateRange: () => any;
setMaxPoints: (v: number) => any;

// label/id key for the fetch (only used in comments/error messages)
idPropName: "portId" | "connectionId" | "datacenterId";
}

export const PortAdapter: ServiceAdapter = {
select: (s) => s.port as BandwidthSliceShape,
fetchThunk: ({ api, tenantId, timeRange, interval, id, startDate, endDate, maxPoints }) =>
  fetchPortPerformance({ 
    api, 
    portId: id, 
    timeRange, 
    interval, 
    tenantId,
    startDate,
    endDate,
    maxPoints
  }),
setTimeRange: setPortTimeRange,
setInterval: setPortInterval,
setUseCustomDateRange: setPortUseCustomDateRange,
setCustomDateRange: setPortCustomDateRange,
clearCustomDateRange: clearPortCustomDateRange,
setMaxPoints: setPortMaxPoints,
idPropName: "portId",
};

export const CloudAdapter: ServiceAdapter = {
select: (s) => s.connection as BandwidthSliceShape,
fetchThunk: ({ api, tenantId, timeRange, interval, id, startDate, endDate, maxPoints }) =>
  fetchCloudPerformance({ 
    api, 
    connectionId: id, 
    timeRange, 
    interval, 
    tenantId,
    startDate,
    endDate,
    maxPoints
  }),
setTimeRange: setCloudTimeRange,
setInterval: setCloudInterval,
setUseCustomDateRange: setCloudUseCustomDateRange,
setCustomDateRange: setCloudCustomDateRange,
clearCustomDateRange: clearCloudCustomDateRange,
setMaxPoints: setCloudMaxPoints,
idPropName: "connectionId",
};

export const InternetAdapter: ServiceAdapter = {
select: (s) => s.connection as BandwidthSliceShape,
fetchThunk: ({ api, tenantId, timeRange, interval, id, destination, startDate, endDate, maxPoints }) =>
  fetchConnectionPerformance({ 
    api, 
    connectionId: id, 
    timeRange, 
    interval, 
    tenantId,
    destination,  // Pass destination if provided for latency filtering
    startDate,
    endDate,
    maxPoints
  }),
setTimeRange: setCloudTimeRange,  // Same as cloud (both use connectionSlice)
setInterval: setCloudInterval,
setUseCustomDateRange: setCloudUseCustomDateRange,
setCustomDateRange: setCloudCustomDateRange,
clearCustomDateRange: clearCloudCustomDateRange,
setMaxPoints: setCloudMaxPoints,
idPropName: "connectionId",
};

export const DatacenterAdapter: ServiceAdapter = {
select: (s) => s.datacenter as BandwidthSliceShape,
fetchThunk: ({ api, tenantId, timeRange, interval, id, startDate, endDate, maxPoints }) =>
  fetchDatacenterPerformance({ 
    api, 
    datacenterId: id, 
    timeRange, 
    interval, 
    tenantId,
    startDate,
    endDate,
    maxPoints
  }),
setTimeRange: setDatacenterTimeRange,
setInterval: setDatacenterInterval,
setUseCustomDateRange: setDatacenterUseCustomDateRange,
setCustomDateRange: setDatacenterCustomDateRange,
clearCustomDateRange: clearDatacenterCustomDateRange,
setMaxPoints: setDatacenterMaxPoints,
idPropName: "datacenterId",
};

export const SERVICE_ADAPTERS: Record<BandwidthService, ServiceAdapter> = {
port: PortAdapter,
cloud: CloudAdapter,
internet: InternetAdapter,
datacenter: DatacenterAdapter,
};

